import { createApiInstance } from './axios-config';
import API_CONFIG from '../../config/apiConfig';

const api = createApiInstance(API_CONFIG.USER_SERVICE_URL);

export interface OrganizationUser {
  id: string;
  email: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: string;
  emailVerified: boolean;
  isActive: boolean;
  isPending?: boolean;
  invitedBy?: string;
}

export interface UserInvitation {
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'member';
  customMessage?: string;
}

export interface OAuthConfig {
  id: string;
  provider: string;
  clientId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export class UserService {
  private baseUrl: string = '/api/v1';
  private currentUserCache: { data: any; timestamp: number } | null = null;
  private CACHE_DURATION = 60000; // Cache for 1 minute

  // Organization User Management
  async getOrganizationUsers(): Promise<{ data: OrganizationUser[] }> {
    // Use cached user data or get from localStorage to avoid hitting /users/me repeatedly
    const storedUser = localStorage.getItem('user');
    let organizationId: string | undefined;
    
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        organizationId = user.organizationId;
      } catch (e) {
        console.error('Failed to parse stored user:', e);
      }
    }
    
    // If no organizationId in localStorage, try to get current user (with caching)
    if (!organizationId) {
      const currentUser = await this.getCurrentUser();
      organizationId = currentUser?.organizationId;
    }
    
    if (!organizationId) {
      throw new Error('User does not belong to an organization');
    }
    
    // Then get the organization members
    // Include pending invitations in the request
    const response = await api.get(`${this.baseUrl}/organizations/${organizationId}/members?includePending=true`);
    
    // Backend now returns an object with members and pendingInvitations
    const members = response.data.members || [];
    const pendingInvitations = response.data.pendingInvitations || [];
    
    // Transform the backend response to match frontend interface
    const transformedMembers = members.map((member: any) => ({
      id: member.id,
      email: member.email,
      fullName: member.full_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
      firstName: member.first_name,
      lastName: member.last_name,
      role: member.role,
      joinedAt: member.joined_at,
      emailVerified: member.email_verified || false,
      isActive: member.is_active !== false,
      isPending: false
    }));
    
    // Transform pending invitations to match the member interface
    const transformedInvitations = pendingInvitations.map((invitation: any) => ({
      id: invitation.id,
      email: invitation.email,
      fullName: invitation.recipient_name || invitation.email,
      firstName: '',
      lastName: '',
      role: invitation.role,
      joinedAt: invitation.invited_at,
      emailVerified: false,
      isActive: false,
      isPending: true,
      invitedBy: invitation.inviter_name || invitation.inviter_email
    }));
    
    // Combine members and pending invitations
    const allMembers = [...transformedMembers, ...transformedInvitations];
    
    return { data: allMembers };
  }

  async inviteUser(invitation: UserInvitation): Promise<any> {
    // Send all three fields for compatibility
    const invitationData = {
      email: invitation.email,
      role: invitation.role,
      firstName: invitation.firstName,
      lastName: invitation.lastName,
      recipientName: `${invitation.firstName} ${invitation.lastName}`.trim()
    };
    
    // Use the new invitations endpoint which properly sends invitation emails
    const response = await api.post(`${this.baseUrl}/invitations`, invitationData);
    
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('organization-users-updated', {
      detail: { action: 'user-invited', email: invitation.email }
    }));
    
    return response.data;
  }

  async updateUserRole(userId: string, role: string): Promise<any> {
    // Use cached organization ID to avoid extra API calls
    const organizationId = await this.getOrganizationId();
    if (!organizationId) {
      throw new Error('User does not belong to an organization');
    }
    
    const response = await api.put(`${this.baseUrl}/organizations/${organizationId}/members/${userId}/role`, { role });
    
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('organization-users-updated', {
      detail: { action: 'role-updated', userId, role, organizationId }
    }));
    
    return response.data;
  }

  async removeUserFromOrganization(userId: string): Promise<void> {
    // Use cached organization ID to avoid extra API calls
    const organizationId = await this.getOrganizationId();
    if (!organizationId) {
      throw new Error('User does not belong to an organization');
    }
    
    await api.delete(`${this.baseUrl}/organizations/${organizationId}/members/${userId}`);
    
    // Clear any cached user data to ensure fresh data on next fetch
    this.currentUserCache = null;
    
    // Clear localStorage cached data
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        // Preserve user data but clear any cached organization member counts
        delete user.organizationMemberCount;
        localStorage.setItem('user', JSON.stringify(user));
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // Dispatch a custom event to notify other components to refresh their data
    window.dispatchEvent(new CustomEvent('organization-users-updated', {
      detail: { action: 'user-removed', userId, organizationId }
    }));
  }

  // Helper method to get organization ID from cache or localStorage
  private async getOrganizationId(): Promise<string | undefined> {
    // Try localStorage first
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user.organizationId) {
          return user.organizationId;
        }
      } catch (e) {
        console.error('Failed to parse stored user:', e);
      }
    }
    
    // Fall back to getCurrentUser (which itself uses caching)
    const currentUser = await this.getCurrentUser();
    return currentUser?.organizationId;
  }

  // OAuth Configuration Management (stored in integration service)
  async getOrganizationOAuthConfigs(): Promise<{ data: OAuthConfig[] }> {
    // This will actually call the integration service
    const integrationApi = createApiInstance(API_CONFIG.INTEGRATION_SERVICE_URL);
    const response = await integrationApi.get('/api/v1/oauth/configs');
    return { data: response.data.configs || [] };
  }

  async deleteOAuthConfig(configId: string): Promise<void> {
    // This will actually call the integration service
    const integrationApi = createApiInstance(API_CONFIG.INTEGRATION_SERVICE_URL);
    await integrationApi.delete(`/api/v1/oauth/configs/${configId}`);
  }

  // User Profile
  async getCurrentUser(): Promise<any> {
    // Check cache first
    if (this.currentUserCache && 
        Date.now() - this.currentUserCache.timestamp < this.CACHE_DURATION) {
      return this.currentUserCache.data;
    }
    
    // Try to get from localStorage first to avoid unnecessary API calls
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        // Cache it
        this.currentUserCache = {
          data: user,
          timestamp: Date.now()
        };
        return user;
      } catch (e) {
        console.error('Failed to parse stored user:', e);
      }
    }
    
    // If not in localStorage, fetch from API
    try {
      const response = await api.get(`${this.baseUrl}/users/me`);
      // Cache the response
      this.currentUserCache = {
        data: response.data,
        timestamp: Date.now()
      };
      return response.data;
    } catch (error: any) {
      // If rate limited, try to use localStorage as fallback
      if (error.response?.status === 429 && storedUser) {
        try {
          return JSON.parse(storedUser);
        } catch (e) {
          // Fall through to throw original error
        }
      }
      throw error;
    }
  }

  async updateProfile(updates: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    settings?: any;
  }): Promise<any> {
    const response = await api.put(`${this.baseUrl}/users/me`, updates);
    return response.data;
  }

  // Password Management
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post(`${this.baseUrl}/users/me/change-password`, {
      currentPassword,
      newPassword,
    });
  }

  async requestPasswordReset(email: string): Promise<void> {
    await api.post(`${this.baseUrl}/auth/forgot-password`, { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await api.post(`${this.baseUrl}/auth/reset-password`, {
      token,
      newPassword,
    });
  }
}

export const userService = new UserService();